import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../layouts/PageLayout';
import BuildBaseIcon from '../Icons/BuildBaseIcon';
import Button from 'components/Button';
import BackgroundSection from 'components/BackgroundSection';
import CardsCarousel from 'components/CardsCarousel';
import HomeFPCarousel from 'containers/HomeFPCarousel';
import EllipseIcon from 'Icons/EllipseIcon';
import TextPage from 'components/TextPage';
import { getHomePageData } from '../api-sanity/api.js';
import RollingMenu from 'components/RollingMenu';
import { useRouteIsLoading } from 'context/RouteIsLoadingContext';
import { useTheme } from 'hooks/useTheme';
import useSectionAnimation from 'hooks/animations/useSectionAnimation';
import usePageLoadProgress from 'hooks/usePageLoadProgress';
import VectorLoader from 'components/VectorLoader';
import useMediaQuery from 'hooks/useMediaQuery';
import { useSanityMetaTags } from 'hooks/useSanityMetaTags';

const pID = {
  P_1: '09c0639e-6ba9-47de-ab78-8ab302fa0ea2',
  P_2: '566f76d5-cd8b-4eea-84fd-13563faeb978',
  P_3: '1457321f-07ad-4e7a-b170-427cd9a53944',
  P_4: 'bc2759df-a0c7-4b52-9e05-710567eadeb4',
};

const homeIds = {
  cardsCarouselId: 'd3c64963-cf45-4bcd-8b51-33ee6be53e4f',
  rollingMenuId: '44466eab-9bf7-4c28-bf60-844d852da17d',
  homeSliderId: 'ebdb22d2-27b2-4e2e-b1bf-607d8ec56ece',
};

const Home = () => {
  const isMobileSize = useMediaQuery();
  const [homeData, setHomeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setIsLoading: setRouteContextIsLoading } = useRouteIsLoading();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  // Fetch and apply meta tags from Sanity
  useSanityMetaTags('home');

  const firstSectionBackgroundRef = useRef(null);
  const firstSectionTitleRef = useRef(null);
  const firstSectionDescriptionRef = useRef(null);
  const firstSectionButtonRef = useRef(null);

  const { loadingProgress, isDataReady, apiDataResults } = usePageLoadProgress(
    (onProgress) => getHomePageData(homeIds, onProgress)
  );

  useEffect(() => {
    if (isDataReady && apiDataResults) {
      const [data] = apiDataResults;
      setHomeData(data);
      setIsLoading(false);
    }
  }, [isDataReady, apiDataResults]);

  useEffect(() => {
    setRouteContextIsLoading(isLoading);
  }, [isLoading, setRouteContextIsLoading]);

  useSectionAnimation({
    backgroundRef: firstSectionBackgroundRef,
    titleRef: firstSectionTitleRef,
    descriptionRef: firstSectionDescriptionRef,
    buttonRef: firstSectionButtonRef,
    imageUrl:
      homeData?.homePageFirstSection?.lightModeImageUrl ||
      homeData?.homePageFirstSection?.darkModeImageUrl,
    isLoading,
  });

  const getParagraphById = (id) => {
    if (!homeData?.textPages) return null;
    return homeData.textPages.find((p) => p._id === id);
  };

  return (
    <PageLayout className='home-page'>
      <div className='relative background-section-container overflow-hidden full-pager'>
        <div
          className='full-height full-width background-section absolute'
          style={{
            backgroundImage: `url(${
              isDarkMode
                ? homeData?.homePageFirstSection?.darkModeImageUrl ||
                  homeData?.homePageFirstSection?.lightModeImageUrl
                : homeData?.homePageFirstSection?.lightModeImageUrl
            })`,
          }}
          ref={firstSectionBackgroundRef}
        />
        <div className='hp-hero-section full-height full-width flex flex-column z-1'>
          <div className='half-hp-hero flex items-end'>
            <p
              ref={firstSectionTitleRef}
              className='img-page-font-grotesk font-white-color z-1'
            >
              {homeData?.homePageFirstSection?.title}
            </p>
          </div>
          <VectorLoader
            className={`${
              homeData?.homePageFirstSection?.description
                ? ''
                : 'absolute center-abs'
            }`}
            progress={loadingProgress}
          />
          <div className='half-hp-hero'>
            <div className='flex flex-column hp-hero-section-content'>
              <p
                ref={firstSectionDescriptionRef}
                className='paragraph-font-grotesk font-white-color'
              >
                {homeData?.homePageFirstSection?.description}
              </p>
              {homeData?.homePageFirstSection?.buttonText &&
                homeData?.homePageFirstSection?.buttonLink && (
                  <Button
                    ref={firstSectionButtonRef}
                    onClick={() =>
                      navigate(homeData.homePageFirstSection.buttonLink)
                    }
                  >
                    {homeData.homePageFirstSection.buttonText}
                  </Button>
                )}
            </div>
          </div>
        </div>
      </div>
      {!isLoading && getParagraphById(pID.P_1) && (
        <TextPage
          key={getParagraphById(pID.P_1)?._id}
          type={getParagraphById(pID.P_1)?.type}
          titleActive={getParagraphById(pID.P_1)?.titleActive}
          titleInactive={getParagraphById(pID.P_1)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_1)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_1)?.paragraphText}
          buttonText={getParagraphById(pID.P_1)?.buttonText}
          buttonLink={getParagraphById(pID.P_1)?.buttonLink}
        />
      )}
      {!isLoading && homeData?.homeSlider?.slides && (
        <HomeFPCarousel panelData={homeData.homeSlider} />
      )}
      {!isLoading && getParagraphById(pID.P_2) && (
        <TextPage
          key={getParagraphById(pID.P_2)?._id}
          type={getParagraphById(pID.P_2)?.type}
          titleActive={getParagraphById(pID.P_2)?.titleActive}
          titleInactive={getParagraphById(pID.P_2)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_2)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_2)?.paragraphText}
          buttonText={getParagraphById(pID.P_2)?.buttonText}
          buttonLink={getParagraphById(pID.P_2)?.buttonLink}
        />
      )}
      {!isLoading && homeData?.cardsCarousel?.images && (
        <CardsCarousel cards={homeData.cardsCarousel.images} />
      )}
      {!isLoading && getParagraphById(pID.P_3) && (
        <TextPage
          key={getParagraphById(pID.P_3)?._id}
          type={getParagraphById(pID.P_3)?.type}
          titleActive={getParagraphById(pID.P_3)?.titleActive}
          titleInactive={getParagraphById(pID.P_3)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_3)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_3)?.paragraphText}
          buttonText={getParagraphById(pID.P_3)?.buttonText}
          buttonLink={getParagraphById(pID.P_3)?.buttonLink}
        />
      )}
      {!isLoading && homeData?.rollingMenu?.data && (
        <RollingMenu rollingMenu={homeData.rollingMenu} />
      )}
      {!isLoading && getParagraphById(pID.P_4) && (
        <TextPage
          key={getParagraphById(pID.P_4)?._id}
          type={getParagraphById(pID.P_4)?.type}
          titleActive={getParagraphById(pID.P_4)?.titleActive}
          titleInactive={getParagraphById(pID.P_4)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_4)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_4)?.paragraphText}
          buttonText={getParagraphById(pID.P_4)?.buttonText}
          buttonLink={getParagraphById(pID.P_4)?.buttonLink}
        />
      )}
      {!isLoading && homeData?.homeLastPage?.title && (
        <BackgroundSection
          imageUrl={
            isDarkMode
              ? homeData.homeLastPage.darkModeImageUrl ||
                homeData.homeLastPage.lightModeImageUrl
              : homeData.homeLastPage.lightModeImageUrl
          }
          className='full-pager page-wrapper relative'
        >
          <div className='flex furniture-content'>
            {!isMobileSize && (
              <div className='furniture-content-icon'>
                <BuildBaseIcon />
              </div>
            )}
            <EllipseIcon
              size={isMobileSize ? '65%' : '25%'}
              style={{
                marginTop: isMobileSize ? 'var(--space-60)' : '0',
              }}
            />
            <div className='furniture-content-text flex flex-column row-gap-30'>
              <p className='img-page-font-grotesk font-white-color'>
                {homeData.homeLastPage.title}
                <br />
                <span className='font-secondary'>
                  {homeData.homeLastPage.titleHighlight}
                </span>
              </p>
              <p className='paragraph-font-grotesk font-white-color'>
                {homeData.homeLastPage.description}
              </p>
              {homeData.homeLastPage.buttonText &&
                homeData.homeLastPage.buttonLink && (
                  <Button
                    showIcon={true}
                    onClick={() => navigate(homeData.homeLastPage.buttonLink)}
                  >
                    {homeData.homeLastPage.buttonText}
                  </Button>
                )}
            </div>
          </div>
        </BackgroundSection>
      )}
    </PageLayout>
  );
};

export default Home;
